<template>
  <section class="page-panel list-page">
    <div class="list-page__header grid grid-gap-16 grid-gap-sm-0 align-items-center">
      <h2 class="col-12 col-sm-6">{{ pageTitle }}</h2>

      <ToolbarActionButton
        v-if="canEditEvents"
        class="col-12 col-sm-6 justify-self-sm-end"
        text="Novi događaj"
        :to="{ name: 'event-create' }"
      />
    </div>

    <Filters
      v-model="state.eventFilters"
      :filter-fields="state.eventFilterFields"
      @change="loadEventsAsync(1)"
    />

    <v-data-table-server
      class="table list-table"
      :headers="state.eventTableHeaders"
      :items="state.events"
      :items-length="state.totalResults"
      :items-per-page="state.itemsPerPage"
      :loading="state.isLoadingEvents"
      :page="state.currentPage"
      item-value="id"
      loading-text="Učitavanje događaja..."
      no-data-text="Nema događaja"
      hide-default-footer
      @click:row="openEventDetail"
    >
      <template #item.status="{ item }">
        <EventStatusChip
          :is-archived="item.isArchived"
          :status="getEventStatus(item)"
        />
      </template>

      <template #item.brandCode="{ item }">
        {{ getEventBrandName(item.brandCode) }}
      </template>

      <template #item.startDate="{ item }">
        {{ formatDate(item.startDate) }}
      </template>

      <template #item.endDate="{ item }">
        {{ formatDate(item.endDate) }}
      </template>

      <template #item.actions="{ item }">
        <v-menu
          content-class="table-menu"
          location="bottom end"
        >
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              aria-label="Otvori akcije"
              class="button table__actions-button"
              icon="mdi-dots-horizontal"
              size="small"
              variant="text"
              @click.stop
            />
          </template>

          <div class="table-menu__content grid-stack grid-gap-16">
            <v-btn
              class="button button--menu"
              :to="{ name: 'event-detail', params: { eventId: item.id } }"
              variant="text"
            >
              Detalji događaja
            </v-btn>
            <v-btn
              v-if="canEditEvents && !item.isArchived"
              class="button button--menu"
              :to="{ name: 'event-edit', params: { eventId: item.id } }"
              variant="text"
            >
              Uredi događaj
            </v-btn>
            <v-btn
              v-if="canEditEvents && !item.isArchived && getEventStatus(item) === 'upcoming'"
              class="button button--menu button--text-danger table-menu__button--danger"
              variant="text"
              @click="openDeleteEventDialog(item)"
            >
              <v-icon
                class="table-menu__icon"
                icon="mdi-trash-can-outline"
                size="16"
              />
              Ukloni događaj
            </v-btn>
            <v-btn
              v-if="canEditEvents && !item.isArchived && getEventStatus(item) === 'past'"
              class="button button--menu"
              variant="text"
              @click="openArchiveEventDialog(item)"
            >
              Arhiviraj događaj
            </v-btn>
            <v-btn
              v-if="canEditEvents && item.isArchived"
              class="button button--menu"
              variant="text"
              @click="setEventArchivedAsync(item, false)"
            >
              Dearhiviraj događaj
            </v-btn>
          </div>
        </v-menu>
      </template>
    </v-data-table-server>

    <Pagination
      :page="state.currentPage"
      :per-page="state.itemsPerPage"
      :total-results="state.totalResults"
      @page-change="loadEventsAsync"
    />

    <AppConfirmDialog
      v-model="state.deleteEventConfirmDialogOpen"
      title="Ukloni događaj?"
      :subtitle="state.selectedEventForAction?.name"
      message="Jeste li sigurni da želite ukloniti ovaj događaj?"
      primary-text="Ukloni"
      secondary-text="Odustani"
      @close="clearSelectedEventForAction"
      @confirm="deleteSelectedEventAsync"
    />

    <AppConfirmDialog
      v-model="state.deleteEventWithInviteesDialogOpen"
      variant="blocked"
      title="Događaj se ne može ukloniti"
      :subtitle="state.selectedEventForAction?.name"
      message="Ovaj događaj ima dodane uzvanike, zato ga nije moguće izbrisati. Možete ukloniti uzvanike i zatim izbrisati događaj."
      primary-text="Razumijem"
      @close="clearSelectedEventForAction"
    />

    <AppConfirmDialog
      v-model="state.archiveEventConfirmDialogOpen"
      title="Arhiviraj događaj?"
      :subtitle="state.selectedEventForAction?.name"
      message="Jeste li sigurni da želite arhivirati ovaj događaj?"
      primary-text="Arhiviraj"
      secondary-text="Odustani"
      @close="clearSelectedEventForAction"
      @confirm="archiveSelectedEventAsync"
    />
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AppConfirmDialog from '../../components/AppConfirmDialog.vue'
import EventStatusChip from '../../components/EventStatusChip.vue'
import Filters from '../../components/Filters.vue'
import Pagination from '../../components/Pagination.vue'
import ToolbarActionButton from '../../components/ToolbarActionButton.vue'
import { canAccessBrand, hasPermission, permissionNames } from '../../auth/authorization'
import { formatDate, getEventBrandName, getEventBrandOptions, getEventStatus, getPageCount } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { getOfflineEventsAsync } from '../../services/offlineService'
import { appStore, showRequestErrorToast, showSuccessToast } from '../../store/appStore'

const route = useRoute()
const router = useRouter()
let latestEventsRequestId = 0

const state = reactive({
  eventFilters: {
    search: null,
    dateRange: null,
    brandCode: null,
    archived: null,
  },
  currentPage: 1,
  itemsPerPage: 10,
  totalResults: 0,
  isLoadingEvents: false,
  selectedEventForAction: null,
  deleteEventConfirmDialogOpen: false,
  deleteEventWithInviteesDialogOpen: false,
  archiveEventConfirmDialogOpen: false,
  events: [],
  eventTableHeaders: [
    { title: 'Naziv događaja', key: 'name', sortable: false, width: '30%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
    { title: 'Marka', key: 'brandCode', sortable: false, width: '17%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
    { title: 'Lokacija', key: 'location', sortable: false, width: '10%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
    { title: 'Datum početka', key: 'startDate', sortable: false, width: '14%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
    { title: 'Datum završetka', key: 'endDate', sortable: false, width: '14%', headerProps: { class: 'text-mid' }, cellProps: { class: 'body-1-regular' } },
    { title: '', key: 'status', sortable: false, align: 'end', width: '10%' },
    { title: '', key: 'actions', sortable: false, align: 'end', width: '5%' },
  ],
  eventFilterFields: [
    {
      key: 'search',
      type: 'search',
      placeholder: 'Pretraži po nazivu ili lokaciji događaja',
      width: '460px',
    },
    {
      key: 'dateRange',
      type: 'dateRange',
      placeholder: 'dd.mm.yyyy - dd.mm.yyyy',
      width: '340px',
    },
    {
      key: 'brandCode',
      type: 'select',
      placeholder: 'MARKA',
      items: getEventBrandOptions().filter(({ value }) => canAccessBrand(value)),
      width: '230px',
    },
    {
      key: 'archived',
      type: 'select',
      placeholder: 'PRIKAŽI',
      items: [
        { title: 'Arhivirani', value: true },
      ],
      width: '230px',
    },
  ],
})

const pageTitle = computed(() => route.meta.title)
const canEditEvents = computed(() => hasPermission(permissionNames.eventsEdit))

onMounted(loadEventsAsync)

watch(() => appStore.isOnline, () => loadEventsAsync(1))

function openEventDetail (_clickEvent, { item }) {
  router.push({ name: 'event-detail', params: { eventId: item.id } })
}

async function loadEventsAsync (page = 1) {
  const requestId = ++latestEventsRequestId
  state.isLoadingEvents = true

  try {
    const { archived, brandCode, dateRange, search } = state.eventFilters

    const eventListResponse = appStore.isOnline
      ? await apiClient.getAsync('/api/events', {
        queryParams: {
          archived,
          brandCode,
          dateRange: dateRange
            ? `${dateRange.from}T00:00:00Z/${dateRange.to ?? dateRange.from}T23:59:59Z`
            : null,
          page,
          perPage: state.itemsPerPage,
          search: search?.trim() || null,
        },
      })
      : await getOfflineEventsAsync({
        filters: { archived, brandCode, dateRange, search },
        page,
        perPage: state.itemsPerPage,
      })

    if (requestId !== latestEventsRequestId) {
      return
    }

    state.events = eventListResponse.results
    state.currentPage = eventListResponse.pagination.page
    state.itemsPerPage = eventListResponse.pagination.perPage
    state.totalResults = eventListResponse.pagination.totalResults
  } catch (error) {
    console.error('Failed to load events.', error)

    if (requestId === latestEventsRequestId) {
      showRequestErrorToast(error, 'Događaje nije bilo moguće učitati.')
    }
  } finally {
    if (requestId === latestEventsRequestId) {
      state.isLoadingEvents = false
    }
  }
}

function openDeleteEventDialog (event) {
  state.selectedEventForAction = event
  state.deleteEventWithInviteesDialogOpen = event.inviteesCount > 0
  state.deleteEventConfirmDialogOpen = event.inviteesCount === 0
}

async function deleteSelectedEventAsync () {
  const eventId = state.selectedEventForAction.id

  try {
    await apiClient.deleteAsync(`/api/events/${eventId}`)

    showSuccessToast('Događaj je uspješno uklonjen.')
    await loadEventsAsync(getPageAfterRemovingResult(
      state.totalResults,
      state.itemsPerPage,
      state.currentPage
    ))
  } catch (error) {
    console.error('Failed to delete event.', error)
    showRequestErrorToast(error, 'Događaj nije bilo moguće ukloniti.')
  }
}

function openArchiveEventDialog (event) {
  state.selectedEventForAction = event
  state.archiveEventConfirmDialogOpen = true
}

async function archiveSelectedEventAsync () {
  await setEventArchivedAsync(state.selectedEventForAction, true)
}

async function setEventArchivedAsync (event, isArchived) {
  const action = isArchived ? 'archive' : 'unarchive'
  const successMessage = isArchived
    ? 'Događaj je uspješno arhiviran.'
    : 'Događaj je uspješno vraćen iz arhive.'
  const errorMessage = isArchived
    ? 'Događaj nije bilo moguće arhivirati.'
    : 'Događaj nije bilo moguće vratiti iz arhive.'

  try {
    await apiClient.patchAsync(`/api/events/${event.id}`, { isArchived })

    showSuccessToast(successMessage)
    await loadEventsAsync(getPageAfterRemovingResult(
      state.totalResults,
      state.itemsPerPage,
      state.currentPage
    ))
  } catch (error) {
    console.error(`Failed to ${action} event.`, error)
    showRequestErrorToast(error, errorMessage)
  }
}

function getPageAfterRemovingResult (totalResults, itemsPerPage, currentPage) {
  const remainingResults = Math.max(0, totalResults - 1)
  const lastPage = getPageCount(remainingResults, itemsPerPage)

  return Math.min(currentPage, lastPage)
}

function clearSelectedEventForAction () {
  state.selectedEventForAction = null
}
</script>
